import React from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  Collapse,
  IconButton,
  useMediaQuery,
} from "@material-ui/core";
import LaunchIcon from "@material-ui/icons/Launch";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getInvoicesByUser } from "../../actions/invoiceActions";

const currencyFmt = (v, code = "USD") =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: code,
    maximumFractionDigits: 0,
  }).format(v || 0);

const computeOutstanding = (invoice = {}) => {
  const total = Number(invoice.total ?? 0);
  const explicitReceived = Number(invoice.totalAmountReceived ?? 0);
  if (explicitReceived) return Math.max(total - explicitReceived, 0);
  const payments = Array.isArray(invoice.paymentRecords)
    ? invoice.paymentRecords.reduce(
        (sum, rec) => sum + Number(rec?.amountPaid || 0),
        0
      )
    : 0;
  return Math.max(total - payments, 0);
};

const isReceivedByToday = (invoice = {}) => {
  if (!invoice.receivedByDate) return false;
  const receivedDate = new Date(invoice.receivedByDate);
  if (Number.isNaN(receivedDate.getTime())) return false;
  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);
  return receivedDate <= todayEnd;
};

export default function OutstandingInvoicesCard({
  title = "Outstanding Invoices",
  currency = "USD",
  autoLoad = true,
  groups: preloadedGroups,
  loading,
}) {
  const history = useHistory();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery("(max-width:600px)");
  const { invoices = [], isLoading: storeIsLoading = false } = useSelector(
    (state) => state?.invoices || {}
  );
  const isLoading = typeof loading === "boolean" ? loading : storeIsLoading;
  const [expandedCustomer, setExpandedCustomer] = React.useState(null);

  const userInfo = React.useMemo(() => {
    if (typeof window === "undefined") return null;
    try {
      const stored = JSON.parse(localStorage.getItem("profile") || "null");
      return stored?.result || stored;
    } catch {
      return null;
    }
  }, []);

  React.useEffect(() => {
    if (Array.isArray(preloadedGroups)) return;
    if (!autoLoad) return;
    const id = userInfo?._id || userInfo?.googleId;
    if (!id) return;
    if (invoices?.length) return;
    dispatch(getInvoicesByUser({ search: id }));
  }, [autoLoad, dispatch, invoices?.length, preloadedGroups, userInfo]);

  const outstandingByCustomer = React.useMemo(() => {
    if (Array.isArray(preloadedGroups)) return preloadedGroups;
    const groups = new Map();
    (invoices || []).forEach((inv) => {
      if (!isReceivedByToday(inv)) return;
      const amountDue = computeOutstanding(inv);
      if (amountDue <= 0) return;
      const status = (inv?.status || "").toLowerCase();
      const isOutstanding =
        status === "unpaid" || status === "partial" || status === "pending";
      if (!isOutstanding) return;
      const customerKey =
        inv?.client?.store || inv?.client?.name || "Unknown customer";
      if (!groups.has(customerKey)) {
        groups.set(customerKey, {
          customer: customerKey,
          total: 0,
          invoices: [],
        });
      }
      groups.get(customerKey).total += amountDue;
      groups.get(customerKey).invoices.push({
        id: inv._id,
        invoiceNumber: inv.invoiceNumber,
        dueDate: inv.dueDate,
        receivedByDate: inv.receivedByDate,
        amountDue,
      });
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        invoices: group.invoices.sort(
          (a, b) => new Date(a.dueDate || 0) - new Date(b.dueDate || 0)
        ),
      }))
      .sort((a, b) => b.total - a.total);
  }, [invoices, preloadedGroups]);

  const totals = React.useMemo(() => {
    const customerCount = outstandingByCustomer.length;
    const invoiceCount = outstandingByCustomer.reduce(
      (sum, group) => sum + group.invoices.length,
      0
    );
    const overallTotal = outstandingByCustomer.reduce(
      (sum, group) => sum + group.total,
      0
    );
    return { customerCount, invoiceCount, overallTotal };
  }, [outstandingByCustomer]);

  const handleToggle = (customer) => {
    setExpandedCustomer((prev) => (prev === customer ? null : customer));
  };

  return (
    <Card variant="outlined" style={{ height: "100%" }}>
      <CardHeader
        title={
          <Box
            component="button"
            type="button"
            onClick={() => history.push("/outstanding-invoices")}
            style={{
              appearance: "none",
              border: 0,
              background: "transparent",
              padding: 0,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              cursor: "pointer",
              color: "inherit",
              font: "inherit",
              fontSize: "inherit",
              fontWeight: "inherit",
              lineHeight: "inherit",
              letterSpacing: "inherit",
              textAlign: "left",
              maxWidth: "100%",
            }}
            aria-label="Open outstanding invoices page"
          >
            <span>{title}</span>
            <OpenInNewIcon style={{ fontSize: 16, color: "#6b7280" }} />
          </Box>
        }
        subheader={
          outstandingByCustomer.length ? (
            <Box
              display="flex"
              alignItems="center"
              style={{
                gap: 8,
                flexWrap: "nowrap",
                whiteSpace: "nowrap",
                fontSize: "inherit",
                lineHeight: "inherit",
              }}
            >
              <span>
                {totals.customerCount} customer
                {totals.customerCount === 1 ? "" : "s"}
              </span>
              <span>•</span>
              <span>
                {totals.invoiceCount} invoice
                {totals.invoiceCount === 1 ? "" : "s"}
              </span>
            </Box>
          ) : undefined
        }
        action={
          totals.invoiceCount ? (
            <Box
              textAlign="right"
              style={{
                minWidth: isMobile ? 88 : 0,
                marginLeft: isMobile ? 8 : 16,
              }}
            >
              <Typography
                variant="body2"
                color="textSecondary"
                style={{
                  fontSize: isMobile ? 11 : undefined,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                }}
              >
                Total Outstanding
              </Typography>
              <Typography
                variant={isMobile ? "subtitle1" : "h6"}
                style={{
                  fontWeight: 600,
                  fontSize: isMobile ? 18 : undefined,
                  lineHeight: 1.15,
                  whiteSpace: "nowrap",
                }}
              >
                {currencyFmt(totals.overallTotal, currency)}
              </Typography>
            </Box>
          ) : null
        }
      />
      <CardContent style={{ maxHeight: 340, overflowY: "auto" }}>
        {isLoading ? (
          <Typography variant="body2" color="textSecondary">
            Loading invoices...
          </Typography>
        ) : outstandingByCustomer.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No outstanding balances.
          </Typography>
        ) : (
          outstandingByCustomer.map((group) => (
            <Box
              key={group.customer}
              borderBottom="1px solid rgba(0,0,0,0.06)"
              py={0.75}
            >
              <Box
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                justifyContent="space-between"
                alignItems={isMobile ? "flex-start" : "center"}
                style={{ gap: isMobile ? 6 : 12, minWidth: 0 }}
              >
                <Box style={{ minWidth: 0 }}>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {group.customer}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {group.invoices.length} invoice
                    {group.invoices.length === 1 ? "" : "s"} outstanding
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" style={{ gap: 8, width: isMobile ? "100%" : "auto" }}>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {currencyFmt(group.total, currency)}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleToggle(group.customer)}
                    style={{
                      transform:
                        expandedCustomer === group.customer
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  >
                    <ExpandMoreIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Collapse in={expandedCustomer === group.customer}>
                <Box mt={0.5}>
                  {group.invoices.map((inv) => (
                    <Box
                      key={inv.id}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      py={0.25}
                      pl={isMobile ? 0 : 1}
                      style={{ cursor: "pointer", gap: 8, minWidth: 0 }}
                      onClick={() => history.push(`/invoice/${inv.id}`)}
                    >
                      <Box style={{ minWidth: 0, flex: "1 1 auto" }}>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          {inv.invoiceNumber || inv.id || "Invoice"}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                          Ship Date:{" "}
                          {inv.receivedByDate
                            ? new Date(inv.receivedByDate).toLocaleDateString()
                            : "No ship date"}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                          Due Date:{" "}
                          {inv.dueDate
                            ? new Date(inv.dueDate).toLocaleDateString()
                            : "No due date"}
                        </Typography>
                      </Box>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-end"
                        style={{ gap: 4, flex: "0 0 auto" }}
                      >
                        <Typography variant="body2">
                          {currencyFmt(inv.amountDue, currency)}
                        </Typography>
                        <LaunchIcon fontSize="small" color="primary" />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Box>
          ))
        )}
      </CardContent>
    </Card>
  );
}
